import type { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'

const WINDOW_MS = 100
const pending = new Map<string, { time: number; promise: Promise<AxiosResponse> }>()

function stableStringify(value: unknown): string {
    if (value === null || typeof value !== 'object') return JSON.stringify(value)
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
    const obj = value as Record<string, unknown>
    return `{${Object.keys(obj).sort().map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`
}

function buildKey(config: AxiosRequestConfig): string {
    return `${config.method ?? 'get'}:${config.baseURL ?? ''}${config.url ?? ''}:${stableStringify(config.params)}:${stableStringify(config.data)}`
}

class DuplicateRequest extends Error {
    constructor(public promise: Promise<AxiosResponse>) {
        super('duplicate request')
    }
}

interface DedupeConfig extends InternalAxiosRequestConfig {
    __dedupe?: { resolve: (r: AxiosResponse) => void; reject: (e: unknown) => void }
    skipDedupe?: boolean
}

export function dedupeRequests(instance: AxiosInstance, excludedUrls: (string | RegExp)[] = []): AxiosInstance {
    instance.interceptors.request.use((config: DedupeConfig) => {
        const url = config.url ?? ''
        if (config.skipDedupe || excludedUrls.some((p) => (p instanceof RegExp ? p.test(url) : url.includes(p)))) {
            return config
        }

        const key = buildKey(config)
        const now = Date.now()
        const hit = pending.get(key)
        if (hit && now - hit.time < WINDOW_MS) {
            return Promise.reject(new DuplicateRequest(hit.promise))
        }

        let resolve!: (r: AxiosResponse) => void
        let reject!: (e: unknown) => void
        const promise = new Promise<AxiosResponse>((res, rej) => { resolve = res; reject = rej })
        pending.set(key, { time: now, promise })
        setTimeout(() => {
            if (pending.get(key)?.promise === promise) pending.delete(key)
        }, WINDOW_MS)

        config.__dedupe = { resolve, reject }
        return config
    })

    instance.interceptors.response.use(
        (response) => {
            (response.config as DedupeConfig).__dedupe?.resolve(response)
            return response
        },
        (error) => {
            if (error instanceof DuplicateRequest) {
                return error.promise
            }
            (error.config as DedupeConfig | undefined)?.__dedupe?.reject(error)
            return Promise.reject(error)
        }
    )

    return instance
}
